/**
 * Cross-branch roll-up view readers (plan 12, Run 16, ML-02/03/04). PostgREST
 * reads of the tenant-isolated (security_invoker RLS) multi-location views that
 * feed the centralized owner dashboard. No Edge Fn needed — the views are read
 * directly and aggregated client-side into a MultiLocationSummary.
 */
import { ApiError, type types } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

/** Live + historical occupancy per branch (v_multilocation_occupancy). */
export async function getMultiLocationOccupancy(
  client: GymSupabaseClient,
  tenantId: string,
): Promise<types.MultiLocationOccupancyRow[]> {
  const { data, error } = await client
    .from('v_multilocation_occupancy')
    .select(
      'tenant_id, location_id, location_name, capacity, live_occupancy, checkins_today, hardware_online, active_device_count',
    )
    .eq('tenant_id', tenantId)
    .order('location_name', { ascending: true });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as types.MultiLocationOccupancyRow[];
}

/** Revenue roll-up per branch + period (v_multilocation_revenue). */
export async function getMultiLocationRevenue(
  client: GymSupabaseClient,
  tenantId: string,
): Promise<types.MultiLocationRevenueRow[]> {
  const { data, error } = await client
    .from('v_multilocation_revenue')
    .select(
      'tenant_id, location_id, location_name, month_bucket, year_bucket, payment_count, gross_revenue',
    )
    .eq('tenant_id', tenantId)
    .order('month_bucket', { ascending: false });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as types.MultiLocationRevenueRow[];
}

/**
 * Compose the centralized owner-dashboard summary from the two roll-up views:
 * one card per branch + tenant totals. Revenue uses the current month bucket only
 * (the dashboard's headline metric); occupancy is live.
 */
export function composeMultiLocationSummary(
  tenantId: string,
  occupancy: types.MultiLocationOccupancyRow[],
  revenue: types.MultiLocationRevenueRow[],
): types.MultiLocationSummary {
  // Current month bucket = the most recent bucket present across the dataset.
  const latestBucket = revenue.reduce<string | null>(
    (acc, r) => (acc === null || r.month_bucket > acc ? r.month_bucket : acc),
    null,
  );
  const revByLocation = new Map<string, number>();
  for (const r of revenue) {
    if (r.location_id === null || r.month_bucket !== latestBucket) continue;
    revByLocation.set(r.location_id, (revByLocation.get(r.location_id) ?? 0) + r.gross_revenue);
  }

  const locations: types.MultiLocationLocationSummary[] = occupancy.map((o) => ({
    location_id: o.location_id,
    location_name: o.location_name,
    capacity: o.capacity,
    live_occupancy: o.live_occupancy,
    checkins_today: o.checkins_today,
    gross_revenue: revByLocation.get(o.location_id) ?? 0,
    hardware_online: o.hardware_online,
  }));

  const totals = locations.reduce(
    (acc, l) => ({
      gross_revenue: acc.gross_revenue + l.gross_revenue,
      live_occupancy: acc.live_occupancy + l.live_occupancy,
      checkins_today: acc.checkins_today + l.checkins_today,
      locations_count: acc.locations_count + 1,
    }),
    { gross_revenue: 0, live_occupancy: 0, checkins_today: 0, locations_count: 0 },
  );

  return { tenant_id: tenantId, locations, totals };
}
